import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../hooks/useTranslation';
import OrgUserManagement from './OrgUserManagement';
import OrgSettings from './OrgSettings';
import BillingManagement from './BillingManagement';
import Payments from '../Payments';
import { Users, Settings, CreditCard, BarChart3 } from 'lucide-react';

const OrgAdminPage = () => {
  const { isOrgAdmin, currentOrg, trialInfo } = useAuth();
  const t = useTranslation();
  const [activeTab, setActiveTab] = useState('users');
  const [showPayments, setShowPayments] = useState(false);

  if (!isOrgAdmin) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
          <h2 className="text-lg font-semibold text-red-800 dark:text-red-300 mb-2">
            {t('orgAdmin.accessDenied')}
          </h2>
          <p className="text-red-700 dark:text-red-400">
            {t('orgAdmin.accessDeniedMessage')}
          </p>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'users', label: t('orgAdmin.tabs.userManagement'), icon: Users },
    { id: 'settings', label: t('orgAdmin.tabs.organizationSettings'), icon: Settings },
    { id: 'billing', label: t('orgAdmin.tabs.billingSubscription'), icon: CreditCard },
  ];

  const isInTrial = trialInfo?.isInTrial || false;

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Payment Modal */}
      {showPayments && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={(e) => {
            // Allow backdrop click to close
            if (e.target === e.currentTarget) {
              setShowPayments(false);
            }
          }}
        >
          <div className="relative w-full max-w-4xl">
            <Payments isUpgrade={true} onClose={() => setShowPayments(false)} />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-slate-800 dark:text-slate-100 mb-2">
          {t('orgAdmin.title')}
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {t('orgAdmin.subtitle', { orgName: currentOrg?.name })}
        </p>
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-700 mb-6">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`
                  flex items-center py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200
                  ${isActive
                    ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                    : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300 dark:hover:border-slate-600'
                  }
                `}
              >
                <Icon className="w-5 h-5 mr-2" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="min-h-[500px]">
        {activeTab === 'users' && <OrgUserManagement />}
        {activeTab === 'settings' && <OrgSettings />}
        {activeTab === 'billing' && (
          <BillingManagement 
            isInTrial={isInTrial} 
            onShowPayments={() => setShowPayments(true)} 
          />
        )}
      </div>
    </div>
  );
};

export default OrgAdminPage; 